/**
 * App configuration.
 *
 * The remote-assistance API base (the Cloudflare Pages Functions origin that
 * serves `/api/config`, `/api/token`, `/api/host/status`) is resolved at
 * runtime (see {@link resolveApiBase}) in priority order:
 *   1. `?api=` query param    - explicit per-open override (confirmed with the
 *                               user for non-localhost targets).
 *   2. `VITE_API_ORIGIN`      - baked at build time.
 *   3. `''` (same origin)     - the deployed default: the client ships from the
 *                               same Pages project that serves the Functions.
 */

export const AppConfig = {
  /**
   * Passthrough camera capture request. Per the passthrough deployment guide:
   * probe with `facingMode: environment`, then pin the explicit deviceId -
   * default constraints only yield 640x480. On an unprovisioned browser (no
   * HEADSET_CAMERA grant) `enumerateDevices()`/`getUserMedia()` HANG rather
   * than reject, so every camera await runs under these timeouts.
   */
  camera: {
    width: 1280,
    height: 960,
    frameRate: 30,
    enumerateTimeoutMs: 5000,
    /** getUserMedia may sit behind a human answering a browser prompt. */
    acquireTimeoutMs: 25000,
  },
  /**
   * Assumed capture-to-detection round-trip (ms) used to place detections
   * through the camera pose from this long ago (see PoseHistory). Tune per
   * deployment; the wire `pts` field enables estimating it dynamically.
   */
  assumedLatencyMs: 200,
  /** Fixed placement distance (m) along the detection ray when no depth hit. */
  placementDistanceMeters: 2.0,
  /**
   * Flip Y when mapping stream boxes to the viewport: image space is y-DOWN
   * from the top-left, the viewport is y-UP. A coordinate-system conversion,
   * not an orientation correction.
   */
  invertY: true,
  /** Training UX tuning (the Unity TrainingPresentationServiceProfile knobs). */
  ux: {
    /** Built-in palette: 0 = Dark·Cyan, 1 = Light·Teal, 2 = Hi-Vis·Orange. */
    themeIndex: 0,
    formDistanceMeters: 1.25,
    windowPlacement: 'fixed' as const,
    /** Keep equal to the detection boxes' placement distance so label and box coincide. */
    labelPlacementDistanceMeters: 2.0,
    /** Start the scenario automatically once streaming begins. */
    autoBegin: true,
    /** Site-root folder holding `<ScenarioName>/<imageRef>.png` step images. */
    imageRoot: '/training',
  },
  /**
   * Edge quality gate thresholds (mean luma, 0..255). The gate only ever MUTES
   * the published track (never unpublishes) - same rule as the Unity client's
   * image-qualifier gate.
   */
  qualifier: { minLuma: 40, maxLuma: 230 },
};

/** Boot must not hang on a stuck fetch - cap API calls and fall through. */
export const CONFIG_FETCH_TIMEOUT_MS = 4000;

/** An API base must be an http(s) origin (empty string = same origin). */
export function isValidApiBase(value: string): boolean {
  if (value === '') return true;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * An `?api=` override redirects where the headset asks for LiveKit credentials,
 * and ultimately where the camera publishes - since the whole UX is "scan a
 * QR", a crafted link could silently point the session at an attacker's
 * backend. Ask the user for non-localhost override targets; the built-in tiers
 * are deployment-trusted and never prompt.
 */
function confirmOverrideTarget(candidate: string): boolean {
  if (typeof confirm !== 'function') return true; // non-interactive context
  const host = new URL(candidate).hostname;
  if (host === 'localhost' || host === '127.0.0.1') return true; // dev flow
  return confirm(`Use "${host}" as the remote-assistance server?\n\n(${candidate})`);
}

function acceptApiBase(candidate: string, source: string): string | null {
  if (!isValidApiBase(candidate)) {
    console.warn(`[WebXRClient] Ignoring invalid API base from ${source}:`, candidate);
    return null;
  }
  if (source === '?api=' && candidate !== '' && !confirmOverrideTarget(candidate)) {
    console.warn('[WebXRClient] ?api= override declined by the user; ignoring it.');
    return null;
  }
  // Trailing slash normalisation so call sites can always do `${base}/api/...`.
  return candidate.replace(/\/+$/, '');
}

interface ImportMetaEnvLike {
  readonly VITE_API_ORIGIN?: string;
  readonly VITE_LIVEKIT_URL?: string;
  readonly VITE_LIVEKIT_TOKEN?: string;
}

function readEnv(): ImportMetaEnvLike {
  // Vite replaces import.meta.env at build time; guard for non-Vite contexts.
  return (import.meta as unknown as { env?: ImportMetaEnvLike }).env ?? {};
}

/**
 * Resolve the API base origin (see the priority order above). `''` means same
 * origin - the normal deployed shape, where the Pages project serving this
 * client also serves the Functions.
 */
export function resolveApiBase(): string {
  const fromQuery =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('api') : null;
  if (fromQuery !== null) {
    const accepted = acceptApiBase(fromQuery, '?api=');
    if (accepted !== null) return accepted;
  }

  const fromEnv = readEnv().VITE_API_ORIGIN;
  if (fromEnv) {
    const accepted = acceptApiBase(fromEnv, 'VITE_API_ORIGIN');
    if (accepted !== null) return accepted;
  }

  return '';
}

/**
 * The absolute `GET /api/config` endpoint for an API base. The room service
 * derives the token and host-status endpoints from this URL's origin, so it
 * must be absolute even for the same-origin default.
 */
export function buildConfigUrl(apiBase: string, origin?: string): string {
  const base = apiBase || origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return new URL('/api/config', base).toString();
}

function queryParam(name: string): string | null {
  return typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get(name) : null;
}

/** The cell id for multi-cell deployments (`?cell=`); empty = the default cell "main". */
export function readCell(): string {
  return queryParam('cell') ?? '';
}

/**
 * A wearer-gate secret override (`?gate=`). Normally the gate arrives inside
 * the served v2 config; this is the development escape hatch.
 */
export function readWearerGateOverride(): string {
  return queryParam('gate') ?? '';
}

/** Development LiveKit literals (zero-infra path); both empty in production. */
export function readDevLiterals(): { readonly url: string; readonly token: string } {
  const env = readEnv();
  return { url: env.VITE_LIVEKIT_URL ?? '', token: env.VITE_LIVEKIT_TOKEN ?? '' };
}
